import { Menu } from 'lucide-react'
import * as React from 'react'
import { Link, useLocation } from 'react-router'
import { Logo } from '../assets/svg/logo'
import { Button } from './ui/button'
import { Sheet, SheetContent, SheetTrigger } from '~/components/ui/sheet'
import { EVENTS } from '~/lib/analytics/events'
import { track } from '~/lib/analytics/track.client'
import { cn } from '~/lib/utils'

interface NavLinkProps {
	to: string
	children: React.ReactNode
	onClick?: () => void
}

function NavLink({ to, children, onClick }: NavLinkProps) {
	const location = useLocation()
	const isCurrentPage =
		to === '/' ? location.pathname === '/' : location.pathname.startsWith(to)

	return (
		<Link
			to={to}
			className={cn(
				'relative font-normal',
				'after:absolute after:bottom-[-4px] after:left-0 after:h-[1px] after:w-full after:content-[""]',
				isCurrentPage && 'after:bg-primary',
			)}
			onClick={onClick}
		>
			{children}
		</Link>
	)
}

export function Nav() {
	const location = useLocation()
	const [isOpen, setIsOpen] = React.useState(false)

	const handleNavClick = (destination: string) => {
		track(EVENTS.NAVIGATION.LINK_CLICK, {
			location: 'main_nav',
			item: destination,
			path: location.pathname,
		})
		setIsOpen(false)
	}

	const handleLogoClick = () => {
		track(EVENTS.NAVIGATION.LINK_CLICK, {
			location: 'main_nav',
			item: 'logo',
			path: location.pathname,
		})
	}

	const handleStartClick = () => {
		track(EVENTS.NAVIGATION.LINK_CLICK, {
			location: 'main_nav',
			item: 'start_button',
			path: location.pathname,
		})
	}

	const logoComponent = (
		<Link
			to="/"
			className="flex items-center hover:opacity-80"
			onClick={handleLogoClick}
		>
			<Logo className="h-[30px] w-auto" />
			<span className="ml-2 text-[20px] font-bold">MoneyDash</span>
		</Link>
	)

	const navLinks = (
		<ul className="flex w-full flex-col items-center space-y-4 md:w-auto md:flex-row md:space-x-8 md:space-y-0">
			<li className="w-full md:w-auto">
				<NavLink to="/" onClick={() => handleNavClick('features')}>
					Features
				</NavLink>
			</li>
			<li className="w-full md:w-auto">
				<NavLink to="/pricing" onClick={() => handleNavClick('pricing')}>
					Pricing
				</NavLink>
			</li>
			<li className="w-full md:w-auto">
				<NavLink to="/support" onClick={() => handleNavClick('support')}>
					Support
				</NavLink>
			</li>
			<li className="w-full md:w-auto">
				<NavLink to="/blog" onClick={() => handleNavClick('blog')}>
					Blog
				</NavLink>
			</li>
			<li className="w-full md:w-auto">
				<NavLink to="/app" onClick={() => handleNavClick('app')}>
					App
				</NavLink>
			</li>
		</ul>
	)

	return (
		<header className="fixed left-0 right-0 top-0 z-50 flex h-[88px] items-center justify-between bg-background px-10 py-8 md:static md:px-56">
			<div className="flex items-center">{logoComponent}</div>
			<div className="flex min-w-[72px] justify-end">
				{location.pathname === '/' && (
					<Button asChild variant="secondary">
						<Link to="/signup" onClick={handleStartClick}>
							Start
						</Link>
					</Button>
				)}
			</div>
			{/* <nav className="hidden md:block">{navLinks}</nav> */}
		</header>
	)
}
